import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { investorContactAPI, urgencyRulesAPI, fundAPI } from '../services/api';
import { Plus, Users, Search, ChevronUp, ChevronDown, Trash2, Eye, Clock, Upload, Settings, Columns, MessageSquare, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useMobile } from '../hooks/useMobile';
import InvestorBulkImportModal from '../components/investors/InvestorBulkImportModal';
import InteractionBulkImportModal from '../components/investors/InteractionBulkImportModal';
import ContactBulkUpdateModal from '../components/investors/ContactBulkUpdateModal';
import UrgencyRulesConfig from '../components/investors/UrgencyRulesConfig';
import { useCurrencyToggle } from '../hooks/useCurrencyToggle';

const levelColorsByIndex = [
  'bg-blue-100 text-blue-700',
  'bg-yellow-100 text-yellow-700',
  'bg-orange-100 text-orange-700',
  'bg-red-100 text-red-700',
  'bg-purple-100 text-purple-700',
  'bg-pink-100 text-pink-700',
  'bg-emerald-100 text-emerald-700',
  'bg-cyan-100 text-cyan-700',
];

// Section definitions for configurable list view.
// The 4 amount columns (Investment Summary) use `fmtAmt(contact, bucketKey)` so the
// list view can render in INR or USD with a free-input exchange rate.
function buildViewSections(fmtAmt) {
  return {
    contact_info: {
      label: 'Contact Information',
      columns: [
        { key: 'name', label: 'Name', render: (c) => [c.salutation, c.first_name, c.middle_name, c.last_name].filter(Boolean).join(' ') },
        { key: 'email', label: 'Email', render: (c) => c.email || '-' },
        { key: 'mobile', label: 'Mobile', render: (c) => c.mobile || '-', hideOnMobile: true },
      ],
    },
    fundraise_coverage: {
      label: 'Fundraise Coverage',
      columns: [
        { key: 'distributor_name', label: 'Distributor', render: (c) => c.distributor_name || '-', hideOnMobile: true },
        { key: 'investor_lead_name', label: 'Investor Lead', render: (c) => c.investor_lead_name || '-', hideOnMobile: true },
        { key: 'coverage_urgency', label: 'Urgency', render: null },
      ],
    },
    interactions: {
      label: 'Interactions',
      columns: [
        { key: 'days_since_last_interaction', label: 'Days Since', render: null },
        { key: 'interaction_count', label: 'Total Interactions', render: (c) => c.interaction_count ?? '-' },
      ],
    },
    investment_summary: {
      label: 'Investment Summary',
      columns: [
        { key: 'total_commitment', label: 'Total Commitment', render: (c) => fmtAmt(c, 'commitment'), align: 'right' },
        { key: 'total_called', label: 'Total Called', render: (c) => fmtAmt(c, 'called'), align: 'right' },
        { key: 'total_contribution', label: 'Total Contribution', render: (c) => fmtAmt(c, 'contribution'), align: 'right' },
        { key: 'total_distribution', label: 'Total Distribution', render: (c) => fmtAmt(c, 'distribution'), align: 'right' },
        { key: 'total_nav', label: 'Total NAV', render: (c) => fmtAmt(c, 'nav'), align: 'right' },
      ],
    },
  };
}

// Module-level enumeration only (render fns no-op). Component overrides via runtimeViewSections.
const VIEW_SECTIONS = buildViewSections(() => '-');

function fmtINR(v) {
  if (v == null || v === 0) return '-';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);
}

// Build flat list of all available fields with section info
const ALL_FIELDS = Object.entries(VIEW_SECTIONS).flatMap(([sectionKey, section]) =>
  section.columns.map(col => ({ ...col, section: sectionKey, sectionLabel: section.label }))
);
const ALL_FIELD_KEYS = ALL_FIELDS.map(f => f.key);

const InvestorContacts = () => {
  const { user } = useAuth();
  const { canDelete, canCreate, canEdit } = usePermissions() || {};
  const isMobile = useMobile();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [urgencyFilter, setUrgencyFilter] = useState('');
  const [fundFilter, setFundFilter] = useState('');
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showBulkImportInteractions, setShowBulkImportInteractions] = useState(false);
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const [showUrgencyRules, setShowUrgencyRules] = useState(false);
  const [urgencyLevels, setUrgencyLevels] = useState(['Low', 'Medium', 'High', 'Very High']);
  const [activeFields, setActiveFields] = useState(() => {
    try {
      const saved = localStorage.getItem('investorContacts_fields');
      return saved ? JSON.parse(saved) : ALL_FIELD_KEYS;
    } catch { return ALL_FIELD_KEYS; }
  });
  const [showFieldPicker, setShowFieldPicker] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Currency + display-unit toggle (persisted to localStorage)
  const currencyToggle = useCurrencyToggle('investorContacts_currency');
  const { formatFromBuckets, controlsUI } = currencyToggle;

  // Runtime VIEW_SECTIONS that uses currency-aware formatters
  const runtimeViewSections = useMemo(() => buildViewSections(
    (c, bucketKey) => {
      if (c && c.totals_by_currency && Object.keys(c.totals_by_currency).length > 0) {
        return formatFromBuckets(c.totals_by_currency, bucketKey);
      }
      const v = c[`total_${bucketKey}`];
      if (v == null || v === 0) return '-';
      return currencyToggle.format(v, 'INR');
    }
  ), [formatFromBuckets, currencyToggle]);

  useEffect(() => {
    fetchContacts();
    fetchUrgencyLevels();
    fetchFunds();
  }, []);

  useEffect(() => {
    localStorage.setItem('investorContacts_fields', JSON.stringify(activeFields));
  }, [activeFields]);

  const fetchContacts = async () => {
    try {
      const res = await investorContactAPI.getAll();
      setContacts(res.data);
    } catch (err) {
      console.error('Failed to fetch investor contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUrgencyLevels = async () => {
    try {
      const res = await urgencyRulesAPI.get();
      setUrgencyLevels(res.data.urgency_levels || ['Low', 'Medium', 'High', 'Very High']);
    } catch {}
  };

  const fetchFunds = async () => {
    try {
      const res = await fundAPI.getAll();
      setFunds(res.data);
    } catch {}
  };

  const getUrgencyColor = (urgency) => {
    const idx = urgencyLevels.indexOf(urgency);
    if (idx === -1) return 'bg-gray-100 text-gray-600';
    return levelColorsByIndex[idx % levelColorsByIndex.length];
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this investor contact?')) return;
    try {
      await investorContactAPI.delete(id);
      setContacts(prev => prev.filter(c => c.id !== id));
      setSelectedIds(prev => { const next = new Set(prev); next.delete(id); return next; });
    } catch {
      alert('Failed to delete investor contact');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} investor contact(s)? This action cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await investorContactAPI.bulkDelete([...selectedIds]);
      setContacts(prev => prev.filter(c => !selectedIds.has(c.id)));
      setSelectedIds(new Set());
    } catch {
      alert('Failed to delete selected contacts');
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pageIds = paginatedContacts.map(c => c.id);
    const allSelected = pageIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(prev => { const next = new Set(prev); pageIds.forEach(id => next.delete(id)); return next; });
    } else {
      setSelectedIds(prev => { const next = new Set(prev); pageIds.forEach(id => next.add(id)); return next; });
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const toggleField = (fieldKey) => {
    setActiveFields(prev =>
      prev.includes(fieldKey) ? prev.filter(k => k !== fieldKey) : [...prev, fieldKey]
    );
  };

  const toggleSectionFields = (sectionKey) => {
    const sectionFieldKeys = VIEW_SECTIONS[sectionKey].columns.map(c => c.key);
    const allSelected = sectionFieldKeys.every(k => activeFields.includes(k));
    if (allSelected) {
      setActiveFields(prev => prev.filter(k => !sectionFieldKeys.includes(k)));
    } else {
      setActiveFields(prev => [...new Set([...prev, ...sectionFieldKeys])]);
    }
  };

  const filteredContacts = useMemo(() => {
    let list = [...contacts];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(c =>
        `${c.first_name} ${c.middle_name || ''} ${c.last_name}`.toLowerCase().includes(term) ||
        (c.email && c.email.toLowerCase().includes(term)) ||
        (c.mobile && c.mobile.includes(term)) ||
        (c.distributor_name && c.distributor_name.toLowerCase().includes(term))
      );
    }
    if (urgencyFilter) {
      list = list.filter(c => c.coverage_urgency === urgencyFilter);
    }
    if (fundFilter) {
      list = list.filter(c => c.invested_fund_ids && c.invested_fund_ids.includes(fundFilter));
    }
    list.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      if (sortField === 'name') {
        aVal = `${a.first_name} ${a.last_name}`;
        bVal = `${b.first_name} ${b.last_name}`;
      }
      if (sortField === 'days_since_last_interaction') {
        aVal = a.days_since_last_interaction ?? 99999;
        bVal = b.days_since_last_interaction ?? 99999;
      }
      if (typeof aVal === 'number' || typeof bVal === 'number') {
        return sortDir === 'asc' ? (aVal || 0) - (bVal || 0) : (bVal || 0) - (aVal || 0);
      }
      if (typeof aVal === 'string') return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return list;
  }, [contacts, searchTerm, urgencyFilter, fundFilter, sortField, sortDir]);

  // Reset to page 1 when filters change
  useEffect(() => { setCurrentPage(1); }, [searchTerm, urgencyFilter, fundFilter, pageSize]);

  const totalPages = pageSize === 'all' ? 1 : Math.ceil(filteredContacts.length / pageSize);
  const paginatedContacts = pageSize === 'all'
    ? filteredContacts
    : filteredContacts.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  // Build visible columns from active fields (preserving section order)
  const visibleColumns = useMemo(() => {
    const allLive = Object.entries(runtimeViewSections).flatMap(([sectionKey, section]) =>
      section.columns.map(col => ({ ...col, section: sectionKey, sectionLabel: section.label }))
    );
    return allLive.filter(f => activeFields.includes(f.key));
  }, [activeFields, runtimeViewSections]);

  if (loading) {
    return <div className="text-center py-12" data-testid="loading-spinner">Loading...</div>;
  }

  return (
    <div data-testid="investor-contacts-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="investor-contacts-title">Investor Contacts</h1>
          <p className="text-gray-600 mt-1">Manage investor contacts and coverage</p>
        </div>
        {canCreate && !isMobile && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowUrgencyRules(true)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
              title="Configure urgency rules"
              data-testid="urgency-rules-btn"
            >
              <Settings size={18} />
            </button>
            <div className="relative">
              <button
                onClick={() => setShowBulkMenu(s => !s)}
                className="inline-flex items-center px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                data-testid="bulk-import-btn"
                aria-haspopup="menu"
                aria-expanded={showBulkMenu}
              >
                <Upload size={18} className="mr-2" />
                Bulk Import
                <ChevronDown size={14} className="ml-1.5" />
              </button>
              {showBulkMenu && (
                <>
                  {/* Click-outside catcher */}
                  <div className="fixed inset-0 z-10" onClick={() => setShowBulkMenu(false)} />
                  <div
                    className="absolute right-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden"
                    role="menu"
                    data-testid="bulk-import-menu"
                  >
                    <button
                      onClick={() => { setShowBulkMenu(false); setShowBulkImport(true); }}
                      className="w-full text-left px-3 py-2.5 hover:bg-blue-50 flex items-start gap-2 border-b border-gray-100"
                      role="menuitem"
                      data-testid="bulk-import-contacts-option"
                    >
                      <Users size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">Investor Contact Information</div>
                        <div className="text-xs text-gray-500">Add new contacts in bulk</div>
                      </div>
                    </button>
                    <button
                      onClick={() => { setShowBulkMenu(false); setShowBulkImportInteractions(true); }}
                      className="w-full text-left px-3 py-2.5 hover:bg-blue-50 flex items-start gap-2"
                      role="menuitem"
                      data-testid="bulk-import-interactions-option"
                    >
                      <MessageSquare size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">Investor Contact Interaction</div>
                        <div className="text-xs text-gray-500">Log meetings / calls against existing contacts</div>
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>
            <button
              onClick={() => setShowBulkUpdate(true)}
              className="inline-flex items-center px-4 py-2 border border-emerald-600 text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors"
              data-testid="bulk-update-contacts-btn"
              title="Update existing contacts in bulk (incremental or replace mode)"
            >
              <RefreshCw size={18} className="mr-2" />
              Bulk Update
            </button>
            <Link
              to="/investors/contacts/create"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              data-testid="create-investor-contact-btn"
            >
              <Plus size={20} className="mr-2" />
              Add Contact
            </Link>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, mobile, distributor..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="search-investor-contacts"
            />
          </div>
          <select
            value={urgencyFilter}
            onChange={e => setUrgencyFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            data-testid="urgency-filter"
          >
            <option value="">All Urgency</option>
            {urgencyLevels.map(level => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
          <select
            value={fundFilter}
            onChange={e => setFundFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            data-testid="fund-filter"
          >
            <option value="">All Funds</option>
            {funds.map(f => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          <div className="relative">
            <button
              onClick={() => setShowFieldPicker(!showFieldPicker)}
              className="inline-flex items-center px-3 py-2 border rounded-lg text-gray-600 hover:bg-gray-50"
              data-testid="field-picker-btn"
              title="Configure visible columns"
            >
              <Columns size={18} />
            </button>
            {showFieldPicker && (
              <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-20 w-64 p-3 max-h-80 overflow-y-auto" data-testid="field-picker-dropdown">
                <p className="text-xs font-semibold text-gray-500 mb-2 uppercase">Visible Columns</p>
                {Object.entries(VIEW_SECTIONS).map(([sectionKey, section]) => {
                  const sectionFieldKeys = section.columns.map(c => c.key);
                  const allSelected = sectionFieldKeys.every(k => activeFields.includes(k));
                  const someSelected = sectionFieldKeys.some(k => activeFields.includes(k));
                  return (
                    <div key={sectionKey} className="mb-2">
                      <label className="flex items-center py-1 cursor-pointer hover:bg-gray-50 rounded px-1 font-medium text-sm text-gray-800">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                          onChange={() => toggleSectionFields(sectionKey)}
                          className="mr-2 h-4 w-4 text-blue-600 rounded"
                        />
                        {section.label}
                      </label>
                      <div className="ml-6">
                        {section.columns.map(col => (
                          <label key={col.key} className="flex items-center py-0.5 cursor-pointer hover:bg-gray-50 rounded px-1">
                            <input
                              type="checkbox"
                              checked={activeFields.includes(col.key)}
                              onChange={() => toggleField(col.key)}
                              className="mr-2 h-3.5 w-3.5 text-blue-600 rounded"
                            />
                            <span className="text-sm text-gray-600">{col.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results count + Page Size + Currency Toggle + Bulk Delete */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-sm text-gray-500">{filteredContacts.length} contact{filteredContacts.length !== 1 ? 's' : ''}</p>
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-gray-400">Show</label>
            <select
              value={pageSize}
              onChange={e => setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="text-xs border border-gray-300 rounded px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
              data-testid="contacts-page-size"
            >
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              <option value="all">All</option>
            </select>
          </div>
          {controlsUI}
        </div>
        {canDelete && selectedIds.size > 0 && (
          <button
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
            className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
            data-testid="bulk-delete-contacts-btn"
          >
            <Trash2 size={16} className="mr-2" />
            {bulkDeleting ? 'Deleting...' : `Delete Selected (${selectedIds.size})`}
          </button>
        )}
      </div>

      {filteredContacts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Users size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No investor contacts yet</h3>
          <p className="text-gray-500 mb-6">Add your first investor contact to get started</p>
          {canCreate && !isMobile && (
            <Link
              to="/investors/contacts/create"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus size={20} className="mr-2" />
              Add Investor Contact
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="investor-contacts-table">
              <thead>
                <tr className="bg-gray-50 border-b text-left text-sm font-medium text-gray-600">
                  {canDelete && (
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={paginatedContacts.length > 0 && paginatedContacts.every(c => selectedIds.has(c.id))}
                        ref={el => { if (el) el.indeterminate = paginatedContacts.some(c => selectedIds.has(c.id)) && !paginatedContacts.every(c => selectedIds.has(c.id)); }}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 text-blue-600 rounded cursor-pointer"
                        data-testid="select-all-contacts"
                      />
                    </th>
                  )}
                  {visibleColumns.map(col => (
                    <th
                      key={col.key}
                      className={`px-4 py-3 cursor-pointer select-none ${col.hideOnMobile ? 'hidden lg:table-cell' : ''} ${col.align === 'right' ? 'text-right' : ''}`}
                      onClick={() => toggleSort(col.key)}
                    >
                      <span className="inline-flex items-center gap-1">{col.label} <SortIcon field={col.key} /></span>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedContacts.map(contact => (
                  <tr
                    key={contact.id}
                    className={`border-b hover:bg-blue-50/50 cursor-pointer transition-colors ${selectedIds.has(contact.id) ? 'bg-blue-50' : ''}`}
                    onClick={() => navigate(`/investors/contacts/${contact.id}`)}
                    data-testid={`investor-contact-row-${contact.id}`}
                  >
                    {canDelete && (
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(contact.id)}
                          onChange={() => toggleSelectOne(contact.id)}
                          className="h-4 w-4 text-blue-600 rounded cursor-pointer"
                          data-testid={`select-contact-${contact.id}`}
                        />
                      </td>
                    )}
                    {visibleColumns.map(col => {
                      // Special renderers
                      if (col.key === 'coverage_urgency') {
                        return (
                          <td key={col.key} className={`px-4 py-3 ${col.hideOnMobile ? 'hidden lg:table-cell' : ''}`}>
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${getUrgencyColor(contact.coverage_urgency)}`}>
                              {contact.coverage_urgency || '-'}
                            </span>
                          </td>
                        );
                      }
                      if (col.key === 'days_since_last_interaction') {
                        return (
                          <td key={col.key} className={`px-4 py-3 ${col.hideOnMobile ? 'hidden lg:table-cell' : ''}`}>
                            {contact.days_since_last_interaction != null ? (
                              <span className="inline-flex items-center gap-1 text-sm">
                                <Clock size={14} className="text-gray-400" />
                                {contact.days_since_last_interaction}d
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                        );
                      }
                      // Default renderer
                      return (
                        <td key={col.key} className={`px-4 py-3 text-sm ${col.hideOnMobile ? 'hidden lg:table-cell' : ''} ${col.align === 'right' ? 'text-right font-medium' : 'text-gray-600'}`}>
                          {col.render ? col.render(contact) : (contact[col.key] || '-')}
                        </td>
                      );
                    })}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/investors/contacts/${contact.id}`); }}
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                          data-testid={`view-contact-${contact.id}`}
                        >
                          <Eye size={16} />
                        </button>
                        {canDelete && (
                          <button
                            onClick={(e) => handleDelete(contact.id, e)}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                            data-testid={`delete-contact-${contact.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination Controls */}
          {pageSize !== 'all' && totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50/50">
              <p className="text-xs text-gray-500">
                Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, filteredContacts.length)} of {filteredContacts.length}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-xs border rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="contacts-prev-page"
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                  .reduce((acc, p, i, arr) => {
                    if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === '...'
                      ? <span key={`dot-${i}`} className="px-1 text-xs text-gray-400">...</span>
                      : <button
                          key={p}
                          onClick={() => setCurrentPage(p)}
                          className={`px-3 py-1 text-xs border rounded ${p === currentPage ? 'bg-blue-600 text-white border-blue-600' : 'hover:bg-gray-100'}`}
                          data-testid={`contacts-page-${p}`}
                        >
                          {p}
                        </button>
                  )}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-xs border rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="contacts-next-page"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bulk Import Modal */}
      <InvestorBulkImportModal
        isOpen={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onSuccess={() => { fetchContacts(); }}
      />

      {/* Interaction Bulk Import Modal */}
      <InteractionBulkImportModal
        isOpen={showBulkImportInteractions}
        onClose={() => setShowBulkImportInteractions(false)}
        onSuccess={() => { fetchContacts(); }}
      />

      {/* Bulk Update Modal */}
      <ContactBulkUpdateModal
        open={showBulkUpdate}
        onClose={() => setShowBulkUpdate(false)}
        onSuccess={() => { fetchContacts(); }}
      />

      {/* Urgency Rules Config Modal */}
      <UrgencyRulesConfig
        isOpen={showUrgencyRules}
        onClose={() => setShowUrgencyRules(false)}
        onSaved={() => { fetchContacts(); fetchUrgencyLevels(); }}
      />

      {/* Click outside to close field picker */}
      {showFieldPicker && (
        <div className="fixed inset-0 z-10" onClick={() => setShowFieldPicker(false)} />
      )}
    </div>
  );
};

export default InvestorContacts;
